import { takeLatest, all, put } from 'redux-saga/effects';

import { requestAndSet, sendToHelium } from 'utils/helpers';

import {
  InvitationConfig,
  InvitationCustomField,
} from '../../types/visitorInvitationTypes';
import { visitorInvitationActions as actions } from '../slices/visitorInvitationSlice';
import { visitsActions } from '../slices/visitsSlice';

interface HeliumWriteResponse {
  id?: number;
  statusCode?: number;
  data?: unknown[];
}

function* createVisitorInvitationSaga(
  action: ReturnType<typeof actions.createVisitorInvitation>,
): Generator {
  const { orgId, payload } = action.payload;
  const result = (yield sendToHelium('newInvitation', [orgId], payload, {
    loadingAction: (v: boolean) =>
      actions.setLoading({ key: 'loadingCreateInvitation', value: v }),
  })) as HeliumWriteResponse;

  if (result?.id || result?.statusCode === 200) {
    yield put(actions.setInvitationSuccess(true));
    yield put(visitsActions.fetchVisits({ orgId }));
  }
}

function* fetchInvitationConfigSaga(
  action: ReturnType<typeof actions.fetchInvitationConfig>,
): Generator {
  yield requestAndSet('getInvitationSettings', [action.payload.orgId], {
    createSetterAction: (res: unknown) =>
      actions.setInvitationConfig(
        (((res as { data?: InvitationConfig[] }).data || [])[0] as
          | InvitationConfig
          | undefined) || ({} as InvitationConfig),
      ),
    loadingAction: (v: boolean) =>
      actions.setLoading({ key: 'loadingInvitationConfig', value: v }),
  });
}

function* updateInvitationConfigSaga(
  action: ReturnType<typeof actions.updateInvitationConfig>,
): Generator {
  const { orgId, config } = action.payload;
  yield sendToHelium('updateInvitationSetting', [orgId], config, {
    loadingAction: (v: boolean) =>
      actions.setLoading({ key: 'loadingUpdateInvitationConfig', value: v }),
  });
  yield put(actions.fetchInvitationConfig({ orgId }));
}

function* fetchInvitationDocumentsSaga(
  action: ReturnType<typeof actions.fetchInvitationDocuments>,
): Generator {
  yield requestAndSet('getInvitationDocument', [action.payload.orgId], {
    createSetterAction: actions.setInvitationDocuments,
    loadingAction: (v: boolean) =>
      actions.setLoading({ key: 'loadingInvitationDocuments', value: v }),
  });
}

function* fetchInvitationCustomFieldsSaga(
  action: ReturnType<typeof actions.fetchInvitationCustomFields>,
): Generator {
  yield requestAndSet(
    'listAllVisitorInvitationFieldsCustom',
    [action.payload.orgId],
    {
      createSetterAction: (res: unknown) =>
        actions.setInvitationCustomFields(
          ((res as { data?: InvitationCustomField[] }).data ||
            []) as InvitationCustomField[],
        ),
      loadingAction: (v: boolean) =>
        actions.setLoading({ key: 'loadingInvitationCustomFields', value: v }),
    },
  );
}

function* createInvitationCustomFieldsSaga(
  action: ReturnType<typeof actions.createInvitationCustomFields>,
): Generator {
  const { orgId, fieldId, required } = action.payload;
  yield sendToHelium(
    'createVisitorInvitationFieldsCustom',
    [orgId, fieldId],
    { required },
    {
      loadingAction: (v: boolean) =>
        actions.setLoading({ key: 'loadingInvitationCustomFields', value: v }),
    },
  );
  yield put(actions.fetchInvitationCustomFields({ orgId }));
}

function* updateInvitationCustomFieldsSaga(
  action: ReturnType<typeof actions.updateInvitationCustomFields>,
): Generator {
  const { orgId, fieldId, required } = action.payload;
  yield sendToHelium(
    'updateVisitorInvitationFieldsCustom',
    [orgId, fieldId],
    { required },
    {
      loadingAction: (v: boolean) =>
        actions.setLoading({ key: 'loadingInvitationCustomFields', value: v }),
    },
  );
  yield put(actions.fetchInvitationCustomFields({ orgId }));
}

function* deleteInvitationCustomFieldsSaga(
  action: ReturnType<typeof actions.deleteInvitationCustomFields>,
): Generator {
  const { orgId, fieldId } = action.payload;
  yield sendToHelium(
    'deleteVisitorInvitationFieldsCustom',
    [orgId, fieldId],
    {},
    {
      loadingAction: (v: boolean) =>
        actions.setLoading({ key: 'loadingInvitationCustomFields', value: v }),
    },
  );
  yield put(actions.fetchInvitationCustomFields({ orgId }));
}

export default function* visitorInvitationSaga(): Generator {
  yield all([
    takeLatest(
      actions.createVisitorInvitation.type,
      createVisitorInvitationSaga,
    ),
    takeLatest(actions.fetchInvitationConfig.type, fetchInvitationConfigSaga),
    takeLatest(actions.updateInvitationConfig.type, updateInvitationConfigSaga),
    takeLatest(
      actions.fetchInvitationDocuments.type,
      fetchInvitationDocumentsSaga,
    ),
    takeLatest(
      actions.fetchInvitationCustomFields.type,
      fetchInvitationCustomFieldsSaga,
    ),
    takeLatest(
      actions.createInvitationCustomFields.type,
      createInvitationCustomFieldsSaga,
    ),
    takeLatest(
      actions.updateInvitationCustomFields.type,
      updateInvitationCustomFieldsSaga,
    ),
    takeLatest(
      actions.deleteInvitationCustomFields.type,
      deleteInvitationCustomFieldsSaga,
    ),
  ]);
}
